<template>
  <div class="container v-content-container">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-cog"></i
          >{{
            `${$label("venue.venue", "Venue")}${
              noDataExist ? "" : ": " + venueName
            }`
          }}
          <select
            v-model="selectedVenueId"
            class="form-control course-select pull-right"
            style="margin-right: 15px;"
          >
            <optgroup
              v-for="facility in facilities"
              :label="facility.name"
              v-bind:key="facility.id"
            >
              <option
                v-for="venue in facility.venues"
                :value="venue.id"
                v-bind:key="venue.id"
                >{{ venue.name }}</option
              >
            </optgroup>
          </select>
        </h1>
      </div>
    </div>

    <navigation-tabs :venue-id="selectedVenueId"></navigation-tabs>

    <div v-show="loading" class="row">
      <div class="col-xs-12">
        <view-loader
          margin-top-pixels="15"
          border-color="primary"
        ></view-loader>
      </div>
    </div>

    <div class="row"><div class="col-xs-12">&nbsp;</div></div>

    <error404 v-if="noDataExist" />

    <template v-show="!loading" v-else>
      <alert-box
        v-show="!loading && !selectedVenue"
        type="warning"
        id="alert-no-venue"
        :closeable="false"
        :show-icon="false"
      >
        {{ $label("user.no_venue_exist", "No venue exist.") }}
      </alert-box>

      <div class="row">
        <div class="col-sm-6" v-if="selectedVenue !== null">
          <h2>
            <i class="fa fa-fw fa-lg fa-location-arrow"></i>
            {{ $label("venue.venue", "Venue") }}
          </h2>
          <p></p>
          <div id="venue-company" class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <section class="col col-8">
                    <text-input
                      v-model="selectedVenue.name"
                      id="venue-name"
                      :label="$label('venue.name', 'Name')"
                      :placeholder="$label('venue.name_placeholder')"
                      :has-error="hasError('name')"
                      :error="getError('name')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateVenue"
                    />
                  </section>
                  <section class="col col-4">
                    <text-input
                      v-model="selectedVenue.short_name"
                      id="venue-short-name"
                      :label="$label('venue.short_name', 'Short name')"
                      :placeholder="$label('venue.short_name', 'Short name')"
                      :has-error="hasError('short_name')"
                      :error="getError('short_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="updateVenue"
                    />
                  </section>
                </div>

                <div class="row">
                  <section class="col col-12">
                    <label class="label">{{
                      $label("venue.description", "Description")
                    }}</label>
                    <label class="textarea">
                      <textarea
                        v-model="selectedVenue.description"
                        :class="{ 'text-input-error': hasError('description') }"
                        :placeholder="
                          $label('venue.description_placeholder', 'Description')
                        "
                        :disabled="updating"
                        id="venue-description"
                        rows="5"
                      >
                      </textarea>
                    </label>
                    <p v-if="hasError('description')" class="text-danger">
                      {{ getError("description") }}
                    </p>
                  </section>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
        <div class="col-sm-6" v-if="selectedVenue !== null">
          <h2>
            <i class="fa fa-fw fa-lg fa-map-marker"></i>
            {{ $label("venue.address", "Address") }}
          </h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <section class="col col-12">
                    <label class="checkbox">
                      <input
                        type="checkbox"
                        id="venue-address-is-identical"
                        v-model="isAddressIdentical"
                      />
                      Identical to location address
                      <i></i>
                    </label>
                  </section>
                </div>
                <div class="row">
                  <!-- BEGIN Country -->
                  <section class="col col-6">
                    <label class="label">{{ $label("common.country") }}</label>

                    <label class="select">
                      <select
                        id="venue-address-country"
                        v-model="selectedVenue.address.country_id"
                        :disabled="updating || isAddressIdentical"
                      >
                        <option
                          v-for="country in countries"
                          :value="country.id"
                          :selected="countryShouldBeSelected(country)"
                          v-bind:key="country.id"
                          >{{
                            $label("common.country_" + country.slug)
                          }}</option
                        >
                      </select>
                      <i></i>
                    </label>

                    <p v-if="hasError('country_id')" class="text-danger">
                      {{ getError("country_id") }}
                    </p>
                  </section>
                  <!-- END Country -->
                  <!-- BEGIN State -->
                  <section class="col col-6">
                    <label class="label">{{
                      $label("common.state", "State")
                    }}</label>

                    <label class="select">
                      <select
                        id="venue-address-state"
                        v-model="selectedVenue.address.state_id"
                        :disabled="updating || isAddressIdentical"
                      >
                        <option
                          v-for="state in statesOfSelectedCountry"
                          :value="state.id"
                          :selected="stateShouldBeSelected(state)"
                          v-bind:key="state.id"
                          >{{ state.name }}</option
                        >
                      </select>
                      <i></i>
                    </label>

                    <p v-if="hasError('state_id')" class="text-danger">
                      {{ getError("state_id") }}
                    </p>
                  </section>
                  <!-- END State -->
                </div>

                <div class="row">
                  <!-- BEGIN City -->
                  <section class="col col-8">
                    <text-input
                      v-model="selectedVenue.address.city"
                      id="venue-address-city"
                      :label="$label('common.city', 'City')"
                      :placeholder="$label('common.city')"
                      :has-error="hasError('city')"
                      :error="getError('city')"
                      :is-disabled="updating || isAddressIdentical"
                      @keyup.native.enter="updateVenue"
                    />
                  </section>
                  <!-- END City -->
                  <!-- BEGIN Post code -->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedVenue.address.post_code"
                      id="venue-address-post-code"
                      :label="$label('common.post_code_short', 'Post code')"
                      :placeholder="$label('common.post_code_short')"
                      :has-error="hasError('post_code')"
                      :error="getError('post_code')"
                      :is-disabled="updating || isAddressIdentical"
                      @keyup.native.enter="updateVenue"
                    />
                  </section>
                  <!-- END Post code -->
                </div>

                <div class="row">
                  <!-- BEGIN Street name -->
                  <section class="col col-8">
                    <text-input
                      v-model="selectedVenue.address.street_name"
                      id="venue-address-street-name"
                      :label="$label('common.street_name_short', 'Street name')"
                      :placeholder="$label('common.street_name_long')"
                      :has-error="hasError('street_name')"
                      :error="getError('street_name')"
                      :is-disabled="updating || isAddressIdentical"
                      @keyup.native.enter="updateVenue"
                    />
                  </section>
                  <!-- END Street name -->
                  <!-- BEGIN Street number -->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedVenue.address.street_number"
                      id="venue-address-street-number"
                      :label="$label('common.street_number', 'Street number')"
                      :placeholder="$label('common.street_number')"
                      :has-error="hasError('street_number')"
                      :error="getError('street_number')"
                      :is-disabled="updating || isAddressIdentical"
                      @keyup.native.enter="updateVenue"
                    />
                  </section>
                  <!-- END Street number -->
                </div>

                <div class="row">
                  <!-- BEGIN additional information -->
                  <section class="col col-12">
                    <label class="label">{{
                      $label("common.additional_address_info_short")
                    }}</label>
                    <label class="textarea">
                      <textarea
                        v-model="selectedVenue.address.additional_information"
                        id="venue-address-additional-information"
                        :class="{
                          'text-input-error': hasError('additional_information')
                        }"
                        :placeholder="
                          $label('common.additional_address_info_long')
                        "
                        :disabled="updating || isAddressIdentical"
                        rows="5"
                      >
                      </textarea>
                    </label>
                    <p
                      v-if="hasError('additional_information')"
                      class="text-danger"
                    >
                      {{ getError("additional_information") }}
                    </p>
                  </section>
                  <!-- END Additional information -->
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6" v-if="selectedVenue !== null">
          <h2>
            <i class="fa fa-fw fa-lg fa-clock-o"></i>
            {{ $label("venue.opening_times", "Opening times") }}
          </h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <section class="col col-12">
                    <label class="checkbox">
                      <input
                        type="checkbox"
                        id="venue-opening-time-is-identical"
                        v-model="isOpeningTimeIdentical"
                      />
                      Identical to location opening times
                      <i></i>
                    </label>
                  </section>
                </div>
                <template v-if="!isDiffDays">
                  <div class="row">
                    <section class="col col-12">
                      <label
                        v-for="weekday in weekdays"
                        :key="weekday.id"
                        class="btn btn-lg btn-default v-form-button"
                        :id="'venue-day-' + weekday.name"
                        :class="{
                          'checked-weekday': weekday.checked,
                          'is-disabled': updating || isOpeningTimeIdentical
                        }"
                        @change="toggleCheckbox(weekday.id)"
                        >{{ weekday.abbr }}
                        <input type="checkbox" class="display-none" />
                      </label>
                    </section>
                  </div>
                  <div class="row">
                    <!-- BEGIN Start time -->
                    <section class="col col-6">
                      <label class="label">{{
                        $label("venue.starts_at", "Starts at")
                      }}</label>
                      <date-picker
                        v-model="starts_at"
                        :format="timeFormat"
                        :readonly="updating || isOpeningTimeIdentical"
                        :disabled="updating || isOpeningTimeIdentical"
                        :placeholder="timeFormat"
                        valueType="format"
                        type="time"
                        class="input"
                        id="venue-starts-at"
                      >
                      </date-picker>
                    </section>
                    <!-- END Start time -->

                    <!-- BEGIN End time -->
                    <section class="col col-6">
                      <label class="label">{{
                        $label("venue.ends_at", "Ends at")
                      }}</label>
                      <date-picker
                        v-model="ends_at"
                        :format="timeFormat"
                        :readonly="updating || isOpeningTimeIdentical"
                        :disabled="updating || isOpeningTimeIdentical"
                        :placeholder="timeFormat"
                        valueType="format"
                        type="time"
                        class="input"
                        id="venue-ends-at"
                      >
                      </date-picker>
                    </section>
                    <!-- END End time -->
                  </div>
                </template>
                <div class="row">
                  <section class="col col-12">
                    <label class="checkbox">
                      <input
                        type="checkbox"
                        id="venue-is-diff-days"
                        v-model="isDiffDays"
                        :disabled="isOpeningTimeIdentical"
                      />
                      Days with different times
                      <i></i>
                    </label>
                  </section>
                </div>
                <template v-if="isDiffDays">
                  <div
                    class="row"
                    v-for="weekday in weekdays"
                    v-bind:key="weekday.id"
                  >
                    <section class="col col-2">
                      <label class="label">{{ space }}</label>
                      <label
                        class="btn btn-lg btn-default v-form-button"
                        :id="'venue-day-' + weekday.name"
                        :class="{
                          'checked-weekday': weekday.checked,
                          'is-disabled': updating || isOpeningTimeIdentical
                        }"
                        @change="toggleCheckbox(weekday.id)"
                        >{{ weekday.abbr }}
                        <input
                          type="checkbox"
                          class="display-none"
                          :disabled="isOpeningTimeIdentical"
                        />
                      </label>
                    </section>
                    <!-- BEGIN Start time -->
                    <section class="col col-5">
                      <label class="label">{{
                        $label("venue.starts_at", "Starts at")
                      }}</label>
                      <date-picker
                        v-model="weekday.starts_at"
                        :format="timeFormat"
                        :readonly="updating || isOpeningTimeIdentical"
                        :disabled="updating || isOpeningTimeIdentical"
                        :placeholder="timeFormat"
                        valueType="format"
                        type="time"
                        class="input"
                        :id="'venue-starts-at-' + weekday.name"
                        :class="{
                          'text-input-error': hasError(
                            'open_times.' + weekday.name + '.starts_at'
                          )
                        }"
                      >
                      </date-picker>

                      <p
                        v-if="
                          hasError('open_times.' + weekday.name + '.starts_at')
                        "
                        class="text-danger"
                      >
                        {{
                          getError("open_times." + weekday.name + ".starts_at")
                        }}
                      </p>
                    </section>
                    <!-- END Start time -->

                    <!-- BEGIN End time -->
                    <section class="col col-5">
                      <label class="label">{{
                        $label("venue.ends_at", "Ends at")
                      }}</label>
                      <date-picker
                        v-model="weekday.ends_at"
                        :format="timeFormat"
                        :readonly="updating || isOpeningTimeIdentical"
                        :disabled="updating || isOpeningTimeIdentical"
                        :placeholder="timeFormat"
                        valueType="format"
                        type="time"
                        class="input"
                        :id="'venue-ends-at-' + weekday.name"
                        :class="{
                          'text-input-error': hasError(
                            'open_times.' + weekday.name + '.ends_at'
                          )
                        }"
                      >
                      </date-picker>

                      <p
                        v-if="
                          hasError('open_times.' + weekday.name + '.ends_at')
                        "
                        class="text-danger"
                      >
                        {{
                          getError("open_times." + weekday.name + ".ends_at")
                        }}
                      </p>
                    </section>
                    <!-- END End time -->
                  </div>
                </template>
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>
      <hr />

      <div class="row" style="margin-bottom: 16px;">
        <div class="col-xs-12 text-center">
          <info-button
            :class="{ 'is-disabled': updating || !selectedVenue }"
            id="save-venue-button"
            icon="fa fa-lg fa-fw fa-save"
            @click.native="updateVenue"
            :text="$label('common.save', 'Save')"
          >
          </info-button>
        </div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>
    </template>
  </div>
</template>

<script>
import Api from "@/api.js";
import FormErrorsMixin from "@/mixins/FormErrors.js";
import swal from "sweetalert";

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/de";

import ViewLoader from "@/components/Common/ViewLoader.vue";
import AlertBox from "@/components/Common/Alerts/AlertBox";
import TextInput from "@/components/Common/Inputs/TextInput.vue";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import NavigationTabs from "@/components/Settings/Venue/NavigationTabs";
import Error404 from "@/components/Common/Error404";

export default {
  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    AlertBox,
    TextInput,
    DatePicker,
    InfoButton,
    NavigationTabs,
    Error404
  },

  data() {
    return {
      facilities: [],
      loading: true,
      space: "\xa0",
      updating: false,
      noDataExist: false,
      countries: [],
      selectedVenue: null,
      selectedVenueId: null,
      serviceCategories: [],
      starts_at: null,
      ends_at: null,
      timeFormat: "HH:mm",
      isDiffDays: false,
      isOpeningTimeIdentical: false,
      isAddressIdentical: false,
      weekdays: [
        {
          id: 0,
          abbr: "MO",
          name: "monday",
          checked: false,
          starts_at: null,
          ends_at: null
        },
        {
          id: 1,
          abbr: "TU",
          name: "tuesday",
          checked: false,
          starts_at: null,
          ends_at: null
        },
        {
          id: 2,
          abbr: "WE",
          name: "wednesday",
          checked: false,
          starts_at: null,
          ends_at: null
        },
        {
          id: 3,
          abbr: "TH",
          name: "thursday",
          checked: false,
          starts_at: null,
          ends_at: null
        },
        {
          id: 4,
          abbr: "FR",
          name: "friday",
          checked: false,
          starts_at: null,
          ends_at: null
        },
        {
          id: 5,
          abbr: "SA",
          name: "saturday",
          checked: false,
          starts_at: null,
          ends_at: null
        },
        {
          id: 6,
          abbr: "SU",
          name: "sunday",
          checked: false,
          starts_at: null,
          ends_at: null
        }
      ],
      validatedInputs: [
        "name",
        "short_name",
        "description",
        "address.country_id",
        "address.state_id",
        "address.city",
        "address.post_code",
        "address.street_name",
        "address.street_number",
        "address.additional_information"
      ]
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedVenueId = vm.$route.params.id;
      vm.getFacilities(() => {
        if (
          vm.selectedVenueId !== null &&
          vm.selectedVenueId !== undefined &&
          vm.selectedVenueId !== "default"
        ) {
          vm.getVenue(() => {
            vm.getCountries(() => {
              //
            });
          });
        }
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    this.getFacilities(() => {
      if (
        this.selectedVenueId !== null &&
        this.selectedVenueId !== undefined &&
        this.selectedVenueId !== "default"
      ) {
        this.getVenue(() => {
          this.getCountries(() => {
            //
          });
        });
      }
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    toggleCheckbox(id) {
      this.weekdays[id].checked = !this.weekdays[id].checked;
      if (this.starts_at) {
        this.weekdays[id].starts_at = this.starts_at;
      }
      if (this.ends_at) {
        this.weekdays[id].ends_at = this.ends_at;
      }
    },

    getFacilities(successCallback) {
      const endpoint = this.$partnerEndpoint("facilities/simplified");

      Api()
        .get(endpoint)
        .then(response => {
          this.facilities = response.data.data;
          if (!this.selectedVenueId || this.selectedVenueId === "default") {
            const selectedFacility = this.facilities.find(
              facility => facility.venues.length > 0
            );
            if (selectedFacility) {
              this.selectedVenueId = selectedFacility.venues[0].id;
            }
          }
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    getVenue(successCallback) {
      this.loading = true;
      const endpoint = this.$partnerEndpoint("venues/" + this.selectedVenueId);

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedVenue = response.data.data;
          if (this.selectedVenue.address === null) {
            this.selectedVenue.address = {
              additional_information: null,
              city: null,
              country_id: null,
              post_code: null,
              state_id: null,
              street_name: null,
              street_number: null
            };
          }
          this.weekdays = this.weekdays.map(weekday => ({
            ...weekday,
            checked: false,
            starts_at: null,
            ends_at: null
          }));
          this.selectedVenue.opening_times.map((time, index) => {
            if (index === 0) {
              this.isDiffDays = false;
              this.starts_at = time.open_time;
              this.ends_at = time.close_time;
            }
            this.weekdays[time.day - 1].checked = true;
            this.weekdays[time.day - 1].starts_at = time.open_time;
            this.weekdays[time.day - 1].ends_at = time.close_time;
            if (
              this.starts_at !== time.open_time ||
              this.ends_at !== time.close_time
            ) {
              this.isDiffDays = true;
              this.starts_at = null;
              this.ends_at = null;
            }
          });
          this.isOpeningTimeIdentical = this.selectedVenue.use_facility_opening_times;
          this.isAddressIdentical = this.selectedVenue.use_facility_address;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateVenue() {
      if (this.loading || this.updating) {
        return;
      }
      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("venues/" + this.selectedVenueId);
      this.selectedVenue.opening_times = this.weekdays
        .filter(
          weekday => weekday.starts_at && weekday.ends_at && weekday.checked
        )
        .map(weekday => ({
          day: weekday.id + 1,
          times: [
            {
              open_time: weekday.starts_at,
              close_time: weekday.ends_at
            }
          ]
        }));
      this.selectedVenue.use_facility_opening_times = this.isOpeningTimeIdentical;
      this.selectedVenue.use_facility_address = this.isAddressIdentical;
      Api()
        .put(endpoint, this.selectedVenue)
        .then(() => {
          this.getFacilities(() => {
            this.getVenue(() => {
              swal({
                title: "Success!",
                text: "The venue was updated.",
                icon: "success"
              });
            });
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#venue-email", 500);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    getCountries(successCallback) {
      Api()
        .get("api/internal/v1/countries")
        .then(response => {
          this.countries = response.data.data;

          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    countryShouldBeSelected(country) {
      return this.selectedVenue.address.country_id == country.id;
    },

    stateShouldBeSelected(state) {
      return this.selectedVenue.address.state_id == state.id;
    }
  },

  computed: {
    venueName() {
      return this.selectedVenue ? this.selectedVenue.name : "";
    },

    statesOfSelectedCountry() {
      for (var i = 0; i < this.countries.length; i++) {
        if (this.countries[i].id != this.selectedVenue.address.country_id) {
          continue;
        }
        return this.countries[i].states;
      }

      return [];
    }
  },

  watch: {
    selectedVenueId(selectedVenueId) {
      this.$router.push({
        name: "VenueGeneralSettings",
        params: { id: selectedVenueId }
      });
    },
    starts_at(starts_at) {
      if (!this.isDiffDays) {
        this.weekdays.map(weekday => {
          if (weekday.checked) {
            weekday.starts_at = starts_at;
          }
        });
      }
    },
    ends_at(ends_at) {
      if (!this.isDiffDays) {
        this.weekdays.map(weekday => {
          if (weekday.checked) {
            weekday.ends_at = ends_at;
          }
        });
      }
    }
  }
};
</script>

<style>
.display-none {
  display: none;
}

.checked-weekday {
  color: #333;
  background-color: #e6e6e6;
  border-color: #adadad;
}
</style>
